import { Bound } from '@blocksuite/affine/global/gfx';
import { nanoid } from '@blocksuite/affine/store';

const replaceText = (text: Record<string, string>, template: any) => {
  if (template != null && typeof template === 'object') {
    if (Array.isArray(template)) {
      template.forEach(v => replaceText(text, v));
      return;
    }
    if (typeof template.insert === 'string') {
      template.insert = text[template.insert] ?? template.insert;
    }
    Object.values(template).forEach(v => replaceText(text, v));
    return;
  }
};

/** Resolves the image URL for a section's image keywords. */
export type SlideImageSource = (keywords: string) => Promise<string>;

const getImages = async (
  images: Record<string, (w: number, h: number) => Promise<string> | string>,

  template: any
): Promise<TemplateImage[]> => {
  const imgs: Record<
    string,
    {
      id: string;
      width: number;
      height: number;
    }
  > = {};

  const run = (data: any) => {
    if (data != null && typeof data === 'object') {
      if (Array.isArray(data)) {
        data.forEach(v => run(v));
        return;
      }
      if (typeof data.caption === 'string') {
        const bound = Bound.deserialize(data.xywh);
        const id = nanoid();
        data.sourceId = id;
        imgs[data.caption] = {
          id: id,
          width: bound.w,
          height: bound.h,
        };
        delete data.caption;
      }
      Object.values(data).forEach(v => run(v));
      return;
    }
  };
  run(template);
  const list = await Promise.all(
    Object.entries(imgs).map(async ([name, data]) => {
      const getImage = images[name];
      if (!getImage) {
        return null;
      }
      try {
        const url = await getImage(data.width, data.height);
        return {
          id: data.id,
          url,
        } as TemplateImage;
      } catch (error) {
        console.error('Error getting image:', error);
        return null;
      }
    })
  );
  return list.filter(v => !!v);
};

export type PPTSection = {
  title: string;
  content: string;
  keywords: string;
};

export type TemplateImage = {
  id: string;
  url: string;
};

type DocTemplate = {
  images: TemplateImage[];
  content: unknown;
};

const createBasicCover = async (
  title: string,
  section1: PPTSection,
  imageFor: SlideImageSource
): Promise<DocTemplate> => {
  const templates = (await import('./templates/cover.json')).default;
  const template = getRandomElement(templates);
  replaceText(
    {
      title: title,
      'section1.title': section1.title,
      'section1.content': section1.content,
    },
    template
  );
  return {
    images: await getImages(
      {
        // cover.json captions its only picture slot "background".
        background: () => imageFor(section1.keywords),
      },
      template
    ),
    content: template,
  };
};

function getRandomElement<T>(arr: T[]): T {
  return JSON.parse(
    JSON.stringify(arr[Math.floor(Math.random() * arr.length)])
  );
}

const basic1section = async (
  title: string,
  section1: PPTSection,
  imageFor: SlideImageSource
): Promise<DocTemplate> => {
  const templates = (await import('./templates/one.json')).default;
  const template = getRandomElement(templates);
  replaceText(
    {
      title: title,
      'section1.title': section1.title,
      'section1.content': section1.content,
    },
    template
  );
  const images = await getImages(
    {
      'section1.image': () => imageFor(section1.keywords),
      'section1.image2': () => imageFor(section1.keywords),
      'section1.image3': () => imageFor(section1.keywords),
    },
    template
  );

  return {
    images: images,
    content: template,
  };
};

const basic2section = async (
  title: string,
  section1: PPTSection,
  section2: PPTSection,
  imageFor: SlideImageSource
): Promise<DocTemplate> => {
  const templates = (await import('./templates/two.json')).default;
  const template = getRandomElement(templates);
  replaceText(
    {
      title: title,
      'section1.title': section1.title,
      'section1.content': section1.content,
      'section2.title': section2.title,
      'section2.content': section2.content,
    },
    template
  );
  return {
    images: await getImages(
      {
        'section1.image': () => imageFor(section1.keywords),
        'section2.image': () => imageFor(section2.keywords),
        background: () =>
          '/ppt-images/background/basic_2_selection_background.png',
      },
      template
    ),
    content: template,
  };
};

const basic3section = async (
  title: string,
  section1: PPTSection,
  section2: PPTSection,
  section3: PPTSection,
  imageFor: SlideImageSource
): Promise<DocTemplate> => {
  const templates = (await import('./templates/three.json')).default;
  const template = getRandomElement(templates);
  replaceText(
    {
      title: title,
      'section1.title': section1.title,
      'section1.content': section1.content,
      'section2.title': section2.title,
      'section2.content': section2.content,
      'section3.title': section3.title,
      'section3.content': section3.content,
    },
    template
  );
  return {
    images: await getImages(
      {
        'section1.image': () => imageFor(section1.keywords),
        'section2.image': () => imageFor(section2.keywords),
        'section3.image': () => imageFor(section3.keywords),
        background: () =>
          '/ppt-images/background/basic_3_selection_background.png',
      },
      template
    ),
    content: template,
  };
};

const basic4section = async (
  title: string,
  section1: PPTSection,
  section2: PPTSection,
  section3: PPTSection,
  section4: PPTSection,
  imageFor: SlideImageSource
): Promise<DocTemplate> => {
  const templates = (await import('./templates/four.json')).default;
  const template = getRandomElement(templates);
  replaceText(
    {
      title: title,
      'section1.title': section1.title,
      'section1.content': section1.content,
      'section2.title': section2.title,
      'section2.content': section2.content,
      'section3.title': section3.title,
      'section3.content': section3.content,
      'section4.title': section4.title,
      'section4.content': section4.content,
    },
    template
  );
  return {
    images: await getImages(
      {
        'section1.image': () => imageFor(section1.keywords),
        'section2.image': () => imageFor(section2.keywords),
        'section3.image': () => imageFor(section3.keywords),
        'section4.image': () => imageFor(section4.keywords),
        background: () =>
          '/ppt-images/background/basic_4_selection_background.png',
      },
      template
    ),
    content: template,
  };
};

export type PPTDoc = {
  isCover: boolean;
  title: string;
  sections: PPTSection[];
};

export const basicTheme = (doc: PPTDoc, imageFor: SlideImageSource) => {
  if (doc.isCover) {
    return createBasicCover(doc.title, doc.sections[0], imageFor);
  }
  if (doc.sections.length === 1) {
    return basic1section(doc.title, doc.sections[0], imageFor);
  }
  if (doc.sections.length === 2) {
    return basic2section(doc.title, doc.sections[0], doc.sections[1], imageFor);
  }
  if (doc.sections.length === 3) {
    return basic3section(
      doc.title,
      doc.sections[0],
      doc.sections[1],
      doc.sections[2],
      imageFor
    );
  }
  return basic4section(
    doc.title,
    doc.sections[0],
    doc.sections[1],
    doc.sections[2],
    doc.sections[3],
    imageFor
  );
};
